import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'loading';
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  dismissible?: boolean;
  icon?: React.ReactNode;
  progress?: boolean;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Toast Provider Component
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: Toast = {
      id,
      duration: 5000,
      dismissible: true,
      progress: true,
      ...toast,
    };
    setToasts(prev => [...prev, newToast]);
    return id;
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const clearToasts = () => {
    setToasts([]);
  };

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearToasts }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

// Individual Toast Component
const ToastItem: React.FC<{ toast: Toast; onRemove: (id: string) => void }> = ({ toast, onRemove }) => {
  const [progress, setProgress] = useState(100);
  const [isPaused, setIsPaused] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!toast.progress || toast.duration === Infinity) return;

    const startTime = Date.now();
    const endTime = startTime + (toast.duration || 5000);

    const updateProgress = () => {
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      const newProgress = (remaining / (toast.duration || 5000)) * 100;
      setProgress(newProgress);

      if (remaining > 0 && !isPaused) {
        timeoutRef.current = setTimeout(updateProgress, 10);
      } else if (remaining <= 0) {
        onRemove(toast.id);
      }
    };

    updateProgress();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [toast.duration, toast.progress, isPaused, onRemove, toast.id]);

  const getToastStyles = () => {
    const baseStyles = "relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm";
    
    switch (toast.type) {
      case 'success':
        return `${baseStyles} bg-gradient-to-r from-green-50 to-emerald-50 border-green-200`;
      case 'error':
        return `${baseStyles} bg-gradient-to-r from-red-50 to-rose-50 border-red-200`;
      case 'warning':
        return `${baseStyles} bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200`;
      case 'info':
        return `${baseStyles} bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200`;
      case 'loading':
        return `${baseStyles} bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200`;
      default:
        return `${baseStyles} bg-white border-gray-200`;
    }
  };

  const getIcon = () => {
    if (toast.icon) return toast.icon;

    const iconClasses = "w-6 h-6";
    
    switch (toast.type) {
      case 'success':
        return (
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#dcfce7' }}>
            <svg className={`${iconClasses}`} style={{ color: '#16a34a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fee2e2' }}>
            <svg className={`${iconClasses}`} style={{ color: '#dc2626' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
            <svg className={`${iconClasses}`} style={{ color: '#d97706' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
      case 'info':
        return (
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#dbeafe' }}>
            <svg className={`${iconClasses}`} style={{ color: '#2563eb' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'loading':
        return (
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#f3e8ff' }}>
            <svg className={`${iconClasses} animate-spin`} style={{ color: '#9333ea' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getTextColor = () => {
    switch (toast.type) {
      case 'success': return '#166534'; // green-800
      case 'error': return '#991b1b'; // red-800
      case 'warning': return '#92400e'; // yellow-800
      case 'info': return '#1e40af'; // blue-800
      case 'loading': return '#581c87'; // purple-800
      default: return '#1f2937'; // gray-800
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.3 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 300, scale: 0.5 }}
      transition={{
        type: "spring",
        stiffness: 500,
        damping: 30,
        duration: 0.3
      }}
      style={{ color: getTextColor() }}
      className={`${getToastStyles().replace(/text-\w+-\d+/, '')} p-4 min-w-[320px] max-w-[420px]`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Progress Bar */}
      {toast.progress && toast.duration !== Infinity && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-black/10">
          <motion.div
            ref={progressRef}
            className={
              toast.type === 'success'
                ? 'h-full bg-gradient-to-r from-green-500 to-emerald-600'
                : 'h-full bg-gradient-to-r from-blue-500 to-purple-500'
            }
            initial={{ width: "100%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1, ease: "linear" }}
          />
        </div>
      )}

      <div className="flex items-start space-x-3">
        {/* Icon */}
        {getIcon()}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="text-sm font-semibold mb-1">{toast.title}</h4>
              {toast.message && (
                <p className="text-sm opacity-90 leading-relaxed">{toast.message}</p>
              )}
            </div>

            {/* Close Button */}
            {toast.dismissible && (
              <button
                onClick={() => onRemove(toast.id)}
                className="ml-3 flex-shrink-0 w-6 h-6 rounded-full bg-black/10 hover:bg-black/20 transition-colors duration-200 flex items-center justify-center"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Action Button */}
          {toast.action && (
            <div className="mt-3">
              <button
                onClick={toast.action.onClick}
                className="text-sm font-medium px-3 py-1.5 rounded-lg bg-black/10 hover:bg-black/20 transition-colors duration-200"
              >
                {toast.action.label}
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Toast Container
const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();

  return createPortal(
    /* Above SideCart overlay (z-[9998]) / panel (z-[9999]) so add-to-cart toasts stay visible */
    <div className="fixed top-4 right-4 z-[10050] space-y-3">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
};

// Toast Hook for Easy Usage
export const useToastNotifications = () => {
  const { addToast, removeToast } = useToast();
  const loadingToastId = React.useRef<string | null>(null);

  return {
    success: (title: string, message?: string, options?: Partial<Toast>) => {
      addToast({ type: 'success', title, message, ...options });
    },
    error: (title: string, message?: string, options?: Partial<Toast>) => {
      addToast({ type: 'error', title, message, ...options });
    },
    warning: (title: string, message?: string, options?: Partial<Toast>) => {
      addToast({ type: 'warning', title, message, ...options });
    },
    info: (title: string, message?: string, options?: Partial<Toast>) => {
      addToast({ type: 'info', title, message, ...options });
    },
    loading: (title: string, message?: string, options?: Partial<Toast>) => {
      const toast = { type: 'loading' as const, title, message, duration: Infinity, ...options };
      const id = addToast({ ...toast });
      loadingToastId.current = id;
    },
    dismissLoading: () => {
      if (loadingToastId.current) {
        removeToast(loadingToastId.current);
        loadingToastId.current = null;
      }
    }
  };
};

export default ToastProvider;
